import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Flex } from "antd";
import QuestionnaireButtonFooterV2 from "components/basic/Footer/QuestionnaireButtonFooterV2";
import ButtonLoading from "components/basic/Spinner/ButtonLoading";
import { ParentProfileType, ProfileType } from "enums/questionnaire";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectDocument } from "redux/reducers/document";
import { selectCurrentNavigation } from "redux/reducers/navigation";
import { setCurrentProfileAnswers } from "redux/reducers/taxProfile";
import { useGetClientTaxProfileQuery, useUpdateClientTaxProfileMutation } from "services/client";
import { IUser } from "types/auth";
import { AccountDetails } from "types/banking";
import { ExpensesCategory, TaxProfile } from "types/client";
import { splitIntoCategoryChunks } from "utils/data";
import { formatAmountWithCommas } from "utils/new-questionnaire";
import { round2Decimals } from "utils/numbers";
import { logError } from "utils/sentry";

import { CategoryEmoji } from "../../../constants/categories";
import { Question, Step } from "../../../constants/questions";
import ExplainerModal from "../../Shared/ExplainerModal";
import TotalClaimedIncomeExpenses from "../../Shared/TotalClaimedIncomeExpenses";

type ProfileExpensesSummaryProps = {
    user: IUser;
    currentStepData: Step;
    data: Question;
    taxProfileType: ProfileType;
    taxParentProfileType: ParentProfileType;
    taxProfile: TaxProfile;
    handleContinue: () => void;
    handlePrev: () => void;
    connectedBankAccounts: AccountDetails[];
};

const ProfileExpensesSummary: React.FC<ProfileExpensesSummaryProps> = ({
    user,
    currentStepData,
    taxProfileType,
    taxProfile,
    handleContinue,
    data,
    handlePrev,
}) => {
    const dispatch = useAppDispatch();
    const [ updateClientTaxProfile] = useUpdateClientTaxProfileMutation();
    const { stepLoading } = useAppSelector(selectCurrentNavigation);
    const { expensesExplainerShown } = useAppSelector(selectDocument);

    const [categoryAmount, setCategoryAmount] = useState<Record<string, string>>({});
    const [currentProfile, setCurrentProfile] = useState<TaxProfile>(taxProfile);
    const { currentProfileAnswers } = useAppSelector(state => state.taxProfile);
    const { data: taxProfileResponse, refetch: refetchTaxProfile } = useGetClientTaxProfileQuery(
        {
            client: user?.id!,
            taxProfileId: taxProfile?._id!,
        },
        {
            skip: !user?.id || !taxProfile, // Skip the query if user or currentProfile is not available
        }
    );

    const {
        isOpened: isExplainerModalOpened,
        open: handleExplainerOpenModal,
        close: handleExplainerCloseModal,
    } = useModalOpener();

    const categoryChunks = useMemo(() => {
        if (!currentProfile?.expenses?.category) return [];

        const categories = Object.entries(currentProfile.expenses.category);

        // Sort categories: Those with `swipedApprovedTransactionsTotal` > 0 come first
        categories.sort(([_, valueA], [__, valueB]) => {
            const hasSwipedA = valueA.swipedApprovedTransactionsTotal > 0 ? 1 : 0;
            const hasSwipedB = valueB.swipedApprovedTransactionsTotal > 0 ? 1 : 0;
            return hasSwipedB - hasSwipedA; // Prioritize categories with swipedApprovedTransactionsTotal > 0
        });

        return splitIntoCategoryChunks(Object.fromEntries(categories), categories.length);
    }, [currentProfile]);

    const editableTotalAmount = useMemo(() => {
        const totalAmount = Object.values(categoryAmount).reduce((acc, value) => {
            const numericValue = parseFloat(value) || 0;
            return acc + numericValue;
        }, 0);

        return round2Decimals(totalAmount);
    }, [categoryAmount]);

    const handleConfirm = useCallback(async () => {
        const updatedCategories = Object.entries(currentProfile.expenses.category).reduce((acc, [categoryName, categoryData]) => {
            acc[categoryName] = {
                ...categoryData,
                confirmedTotal: Number(categoryAmount[categoryName] || "0"),
            };
            return acc;
        }, {} as Record<string, ExpensesCategory>);

        await updateClientTaxProfile({
            taxProfileId: currentProfile._id,
            client: currentProfile.client,
            expenses: {
                ...currentProfile.expenses,
                confirmedTotal: editableTotalAmount,
                category: updatedCategories,
            },

        }).unwrap()
            .then(() => {
                refetchTaxProfile?.();
                handleContinue();
            })
            .catch(error => {
                logError(error);
            });
    }, [categoryAmount, handleContinue, currentProfile, updateClientTaxProfile, refetchTaxProfile, editableTotalAmount]);

    useEffect(() => {
        if (currentProfile && currentProfile.expenses && currentProfile.expenses.category) {
            const initialAmounts = Object.entries(currentProfile.expenses.category).reduce((acc, [categoryName, categoryData]) => {
                acc[categoryName] = Number(categoryData.confirmedTotal || categoryData.swipedApprovedTransactionsTotal || "0.00").toFixed(2);
                return acc;
            }, {} as Record<string, string>);

            setCategoryAmount(initialAmounts);
        }
    }, [currentProfile]);

    useEffect(() => {
        if (taxProfileResponse?.taxProfile) {
            setCurrentProfile(taxProfileResponse.taxProfile);
        }
    }, [taxProfileResponse]);

    useEffect(() => {
        const updatedAnswers = currentProfileAnswers.map(q => ({ ...q })); // ✅ Deep copy objects
        const existingIndex = updatedAnswers.findIndex(q => q.question === data.question);
        const updatedAnswer = { question: data.question, answer: editableTotalAmount, type: data.type || "", id: data.id || "" };
        if (existingIndex > -1) {
            updatedAnswers[existingIndex] = updatedAnswer;
        } else {
            updatedAnswers.push(updatedAnswer);
        }
        dispatch(setCurrentProfileAnswers(updatedAnswers));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableTotalAmount]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!expensesExplainerShown) {
                handleExplainerOpenModal();
            }
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        refetchTaxProfile?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (

        <div className="new-questionnaire__income-expenses-container">

            <TotalClaimedIncomeExpenses taxProfileType={taxProfileType} totalAggregatedAmount={parseFloat(editableTotalAmount.toString() || "0.00")} title={"Total Claimed Business Expenses"} infoText={"<div>Unsure what expenses are allowable? <br><a>Learn more</a></br></div>"}></TotalClaimedIncomeExpenses>

            <div className="new-questionnaire__income-report__wrapper mt20">
                <div className={"new-questionnaire__expense-report"}>
                    <div className="w100">
                        {categoryChunks.map((categoryChunk, index) => (
                            <div className={"new-questionnaire__expense-report-card"} key={`${index}-${categoryChunk}`}>
                                {Object.entries(categoryChunk).map(([categoryName]) => (
                                    <div key={`${index}-${categoryName}`} className="new-questionnaire__amount-card-wrapper">
                                        {CategoryEmoji[taxProfileType]?.[categoryName] ? <div className="flex-row center fs16"><img className="mr6" src={CategoryEmoji[taxProfileType]?.[categoryName]} alt={categoryName} /> {categoryName}</div> : categoryName}
                                        <div className="fs16 fw500">£{categoryAmount[categoryName] ? formatAmountWithCommas(categoryAmount[categoryName]) : "0.00"}</div>
                                    </div>
                                ))}
                            </div>

                        ))}
                    </div>
                </div>

                <div className="mt100">
                    <QuestionnaireButtonFooterV2>
                        <Flex justify="space-between" align="center" className="w100 p20" gap={12}>
                            <button
                                className={"shared__secondary-button"}
                                onClick={() => {
                                    handlePrev();
                                }}
                            >
                                Back
                            </button>
                            <button
                                className={`shared__primary-button maxWidth160px ${!stepLoading ? "" : "disabled"}`}
                                disabled={stepLoading}
                                onClick={() => {
                                    handleConfirm();
                                }}
                            >
                                {stepLoading ? <ButtonLoading/>
                                    : <span>{"Confirm"}</span>}
                            </button>
                        </Flex>
                    </QuestionnaireButtonFooterV2>
                </div>
                {isExplainerModalOpened && (
                    <ExplainerModal
                        isOpen={isExplainerModalOpened}
                        closeModal={handleExplainerCloseModal}
                        subTitle={`${currentStepData.title} - ${taxProfile.name}`}
                    />
                )}
            </div>
        </div>
    );
};

export default ProfileExpensesSummary;
